import { useEffect } from 'react';
import { RedirectError } from '@antimatterhq/ui/hooks/auth/useRedirectHandler';
import { Text } from '@antimatterhq/ui/components/typography';
import { Icon } from '@antimatterhq/ui/components/icon';
import Spacer from '@antimatterhq/ui/components/spacer';
import { GlobalError } from '@antimatterhq/ui/components/globalError';
import {
  prefixStorageKey,
  deleteLocalStorageValue,
} from '@antimatterhq/ui/utils/localStorage';
import { datadogRum } from '@datadog/browser-rum';

const WorkspaceNotProvisioned: React.FC = () => {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <div className="flex h-full w-full flex-col items-center justify-center">
        <Icon icon="lucide:shield-plus" className="h-24 w-24 text-gray-400" />
        <Spacer size="lg" />
        <Text size="lg" semiBold className="text-gray-500">
          Welcome to Security Lakehouse
        </Text>
        <Text className="max-w-[700px] text-center text-gray-500">
          Your organization’s security lakehouse has not been provisioned. Talk
          to your Databricks administrator to make sure they have completed the
          setup for your organization’s security lakehouse.
        </Text>
      </div>
    </div>
  );
};

export const RedirectErrorMessage: React.FC<{
  error: RedirectError;
  initiateAuthentication: () => Promise<void>;
  prefix: 'standalone' | 'client';
}> = ({ error, initiateAuthentication, prefix }) => {
  useEffect(() => {
    deleteLocalStorageValue(
      prefixStorageKey('previousRedirectTimestamps', prefix),
    );
  }, [prefix]);

  useEffect(() => {
    datadogRum.addError(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isWorkspaceNotProvisionedError =
    error.resourceType === 'InstanceIDLookupRecord';

  if (isWorkspaceNotProvisionedError) {
    return <WorkspaceNotProvisioned />;
  }

  const onRetry = async () => {
    await initiateAuthentication();
  };

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <GlobalError
        title={error.message}
        description={
          <Text>
            Please try again. If the problem persists,{' '}
            <a
              className="text-blue-300 underline hover:text-blue-400"
              href="mailto:support@antimatter.io"
            >
              contact support
            </a>
            .
          </Text>
        }
        onRetry={onRetry}
        retryText="Retry"
      />
    </div>
  );
};
